import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  UntypedFormControl,
  ValidatorFn,
  UntypedFormArray,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { ItfgDateAdapter } from '@app/core/adapters/date-adapter.service';
import { BlacklistService } from '../../../core/services/blacklist.service';
import { IdentifierValidation } from '../../../core/validation/identifier-validation';
import { NotificationService } from '../../../core/services/notification.service';
import {
  catchError,
  debounceTime,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { Brand } from '@app/core/types/brand';
import { BrandService } from '@app/core/services/brand.service';
import { BlacklistItem } from '@app/core/types/blacklist-item';
import { BRAND_CONFIG } from '../../../config/app-config';

const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'itfg-blacklist-edit',
  templateUrl: './blacklist-edit.component.html',
  styleUrls: ['./blacklist-edit.component.scss'],
  standalone: false,
  providers: [
    { provide: DateAdapter, useClass: ItfgDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
})
export class BlacklistEditComponent implements OnInit, OnDestroy {
  public blacklistEditForm: UntypedFormGroup;
  public allBrandsControl: UntypedFormControl;
  public globalRowForm: UntypedFormGroup;
  public brandRowsForm: UntypedFormArray;
  public blacklistFormArr: UntypedFormArray;
  public blacklistTypes: any = [];
  public blacklistOriginalTypes: any = [];
  public blacklistEditData: any;
  public brands: Brand[] = [];
  public existingRecords: BlacklistItem[] = [];
  public existingGlobalRecord: BlacklistItem = null;
  public existingByBrandId: { [brandId: number]: BlacklistItem } = {};
  public identityFixed: boolean;
  public isNewItem: boolean;
  public selectedBlacklistType: string;
  public currentDate: Date;
  public gridSecondColumnTranslations: any;
  public titleLabelTranslations: any;
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  invalidValues = '';
  existingValuesWarnings: {
    value: string;
    brands: { logo: string; name: string }[];
  }[] = [];
  readonly EXISTING_VALUES_CHECK_LIMIT: number = 50;
  private checkExistingValues$ = new Subject<string[]>();
  _unsubscribe = new Subject<void>();
  validators: ValidatorFn[] = [
    IdentifierValidation.onlyNumbers,
    IdentifierValidation.isValidEgn,
    Validators.minLength(10),
    Validators.maxLength(10),
  ];

  constructor(
    public dialogRef: MatDialogRef<BlacklistEditComponent>,
    @Inject(MAT_DIALOG_DATA) blacklistEditData: any,
    public formBuilder: UntypedFormBuilder,
    public translate: TranslateService,
    public breakpointObserver: BreakpointObserver,
    public blacklistService: BlacklistService,
    public notifications: NotificationService,
    public brandService: BrandService
  ) {
    this.blacklistEditData = blacklistEditData;
    this.blacklistEditForm = this.createForm();
    this.allBrandsControl = this.formBuilder.control(false);
    this.globalRowForm = this.createRowForm();
    this.globalRowForm.get('expire').enable();
    this.globalRowForm.get('notes').enable();
    this.brandRowsForm = this.formBuilder.array([]);
    this.blacklistFormArr = this.formBuilder.array([], Validators.required);
    this.currentDate = new Date();
    this.selectedBlacklistType = '1';
    this.gridSecondColumnTranslations = {
      '1': 'global.personalId',
      '2': 'clients.employer',
      '3': 'global.phoneNumberWithPrefix',
    };
    this.titleLabelTranslations = {
      '1': 'global.personalId',
      '2': 'clients.employer',
      '3': 'global.phoneNumber',
    };

    this.identityFixed = !!this.blacklistEditData?.blacklistType?.id;
    this.isNewItem = !this.identityFixed;

    if (this.identityFixed) {
      this.selectedBlacklistType =
        this.blacklistEditData.blacklistType.id.toString();
      this.blacklistEditForm.patchValue({
        blacklistType: this.blacklistEditData.blacklistType.id,
        value: this.blacklistEditData.value || '',
      });
      this.matchValues([this.blacklistEditData.value || '']);
    }
  }

  ngOnInit() {
    this.getBlacklistTypeData();
    this.loadBrandsAndRecords();

    if (!this.identityFixed) {
      this.blacklistEditForm.patchValue({ blacklistType: '1' });
      this.blacklistEditForm
        .get('value')
        .valueChanges.pipe(debounceTime(500), takeUntil(this._unsubscribe))
        .subscribe((change) => {
          const parsedChange = this.parseValues(change);
          this.matchValues(parsedChange);
          if (this.blacklistFormArr.invalid) {
            this.setErrorValues();
          }
          this.triggerExistingValuesCheck();
        });

      this.watchForExistingValues();
    }
  }

  setAlreadyBlacklistedError(hasExistingValues: boolean): void {
    const valueControl = this.blacklistEditForm.get('value');

    if (hasExistingValues) {
      valueControl.setErrors({
        ...(valueControl.errors || {}),
        alreadyBlacklisted: true,
      });
      valueControl.markAsTouched();
    } else if (valueControl.hasError('alreadyBlacklisted')) {
      const { alreadyBlacklisted, ...remainingErrors } =
        valueControl.errors || {};
      valueControl.setErrors(
        Object.keys(remainingErrors).length > 0 ? remainingErrors : null
      );
    }
  }

  triggerExistingValuesCheck(): void {
    const validValues: string[] = this.blacklistFormArr.controls
      .filter((control) => control.valid)
      .map((control) => control.value)
      .slice(0, this.EXISTING_VALUES_CHECK_LIMIT);

    this.checkExistingValues$.next(validValues);
  }

  watchForExistingValues(): void {
    this.checkExistingValues$
      .pipe(
        switchMap((values: string[]) => {
          const typeName: string = this.getBlaclistTypeName();
          if (!values.length || !typeName) {
            return of([]);
          }

          return forkJoin(
            values.map((value: string) =>
              this.blacklistService
                .getAllByTypeAndValue(typeName, value)
                .pipe(catchError(() => of([])))
            )
          ).pipe(
            map((results: BlacklistItem[][]) =>
              results
                .map((records: BlacklistItem[], index: number) => ({
                  value: values[index],
                  records,
                }))
                .filter((entry) => entry.records.length > 0)
                .map((entry) => {
                  const seenNames = new Set<string>();
                  return {
                    value: entry.value,
                    brands: entry.records
                      .map((record: BlacklistItem) =>
                        record.brand
                          ? {
                              logo: BRAND_CONFIG[record.brand.code]?.logo,
                              name:
                                BRAND_CONFIG[record.brand.code]?.name ||
                                record.brand.code,
                            }
                          : {
                              logo: 'assets/images/itf.png',
                              name: this.translate.instant('brands.allBrands'),
                            }
                      )
                      .filter((brand) => {
                        if (seenNames.has(brand.name)) {
                          return false;
                        }
                        seenNames.add(brand.name);
                        return true;
                      }),
                  };
                })
            )
          );
        }),
        takeUntil(this._unsubscribe)
      )
      .subscribe(
        (
          warnings: {
            value: string;
            brands: { logo: string; name: string }[];
          }[]
        ) => {
          this.existingValuesWarnings = warnings;
          this.setAlreadyBlacklistedError(warnings.length > 0);
        }
      );
  }

  createForm(): UntypedFormGroup {
    return this.formBuilder.group({
      blacklistType: ['', [Validators.required]],
      value: [''],
    });
  }

  createRowForm(): UntypedFormGroup {
    return this.formBuilder.group({
      checked: [false],
      expire: [{ value: null, disabled: true }],
      notes: [{ value: '', disabled: true }],
    });
  }

  brandRowAt(index: number): UntypedFormGroup {
    return this.brandRowsForm.at(index) as UntypedFormGroup;
  }

  loadBrandsAndRecords(): void {
    this.isLoading = true;

    const requests: {
      brands: Observable<Brand[]>;
      records?: Observable<BlacklistItem[]>;
    } = {
      brands: this.brandService.getBrandList(),
    };

    if (this.identityFixed) {
      const typeName$: Observable<string> = this.blacklistEditData.blacklistType
        .name
        ? of(this.blacklistEditData.blacklistType.name)
        : this.blacklistService
            .getBlacklistTypes()
            .pipe(
              map(
                (types) =>
                  types.find(
                    (type) =>
                      type.id === this.blacklistEditData.blacklistType.id
                  )?.name
              )
            );

      requests.records = typeName$.pipe(
        switchMap((typeName: string) =>
          this.blacklistService.getAllByTypeAndValue(
            typeName,
            this.blacklistEditData.value
          )
        ),
        switchMap((records: BlacklistItem[]) =>
          records && records.length > 0
            ? of(records)
            : this.getExistingRecordsBySearch$()
        )
      );
    }

    forkJoin(requests)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: ({ brands, records }) => {
          this.brands = brands;
          this.buildBrandRows();
          if (records) {
            this.applyExistingRecords(records);
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  getExistingRecordsBySearch$(): Observable<BlacklistItem[]> {
    const criteria = [
      {
        key: 'blacklistType.id',
        operation: '=',
        value: this.blacklistEditData.blacklistType.id.toString(),
      },
      {
        key: 'value',
        operation: ':',
        value: [this.blacklistEditData.value],
      },
    ];

    return this.blacklistService
      .getBlacklist({ criteria: JSON.stringify(criteria) })
      .pipe(map((page) => page?.content || []));
  }

  buildBrandRows(): void {
    this.brandRowsForm.clear();
    this.brands.forEach((brand: Brand) => {
      const rowForm = this.createRowForm();

      rowForm
        .get('checked')
        .valueChanges.pipe(takeUntil(this._unsubscribe))
        .subscribe((checked: boolean) => {
          if (checked) {
            rowForm.get('expire').enable({ emitEvent: false });
            rowForm.get('notes').enable({ emitEvent: false });
          } else {
            rowForm.get('expire').disable({ emitEvent: false });
            rowForm.get('notes').disable({ emitEvent: false });
          }
        });

      this.brandRowsForm.push(rowForm);
    });
  }

  applyExistingRecords(records: BlacklistItem[]): void {
    this.existingRecords = records || [];
    this.existingGlobalRecord =
      this.existingRecords.find((record: BlacklistItem) => !record.brand) ||
      null;
    this.existingByBrandId = {};
    this.existingRecords
      .filter((record: BlacklistItem) => !!record.brand)
      .forEach((record: BlacklistItem) => {
        this.existingByBrandId[record.brand.id] = record;
      });

    this.isNewItem = this.existingRecords.length === 0;

    if (this.existingGlobalRecord) {
      this.allBrandsControl.setValue(true);
      this.globalRowForm.patchValue({
        expire: this.existingGlobalRecord.expire
          ? new Date(this.existingGlobalRecord.expire)
          : null,
        notes: this.existingGlobalRecord.notes || '',
      });
    }

    this.brands.forEach((brand: Brand, index: number) => {
      const record: BlacklistItem = this.existingByBrandId[brand.id];
      if (record) {
        const rowForm = this.brandRowAt(index);
        rowForm.get('checked').setValue(true);
        rowForm.patchValue({
          expire: record.expire ? new Date(record.expire) : null,
          notes: record.notes || '',
        });
      }
    });
  }

  get valueLabelKey(): string {
    return this.titleLabelTranslations[this.selectedBlacklistType];
  }

  isMobile() {
    return this.breakpointObserver.isMatched('(max-width: 510px)');
  }

  getBlacklistTypeData() {
    this.blacklistService.getBlacklistTypes().subscribe((response) => {
      this.blacklistTypes = response;

      for (let i = 0; i < response.length; i++) {
        this.blacklistOriginalTypes[i] = Object.assign({}, response[i]);
      }

      this.translate
        .get(['clients.civil', 'clients.employer', 'clients.phones'])
        .subscribe((translation) => {
          if (this.blacklistTypes.length > 0 && this.blacklistTypes[0].id) {
            this.blacklistTypes[0].name = translation['clients.civil'];
            this.blacklistTypes[1].name = translation['clients.employer'];
            this.blacklistTypes[2].name = translation['clients.phones'];
            if (!this.identityFixed) {
              this.blacklistEditForm.patchValue({
                blacklistType: this.blacklistTypes[0].id,
              });
            }
          }
        });
    });
  }

  selectedValueChanged(value?) {
    const validators: ValidatorFn[] = [];
    this.selectedBlacklistType = this.blacklistEditForm.value.blacklistType;

    if (this.blacklistEditForm.value.blacklistType === 1) {
      validators.push(
        IdentifierValidation.onlyNumbers,
        IdentifierValidation.isValidEgn,
        Validators.minLength(10),
        Validators.maxLength(10)
      );
    } else if (this.blacklistEditForm.value.blacklistType === 2) {
      validators.push(IdentifierValidation.isValidEik);
      validators.push(Validators.minLength(0));
    } else if (this.blacklistEditForm.value.blacklistType === 3) {
      validators.push(Validators.pattern(/^\+359/));
      validators.push(Validators.minLength(12));
    }

    this.blacklistEditForm.controls.value.setValue(
      this.blacklistEditData?.value || ''
    );
    this.validators = validators;
    this.invalidValues = '';
    this.existingValuesWarnings = [];
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onSubmit(event) {
    const allBrands: boolean = this.allBrandsControl.value;
    const checkedBrandIndexes: number[] = this.brands
      .map((brand: Brand, index: number) => index)
      .filter(
        (index: number) => this.brandRowAt(index).get('checked').value === true
      );

    if (
      this.blacklistEditForm.invalid ||
      (!this.identityFixed && this.blacklistFormArr.invalid)
    ) {
      this.notifications.showLocalizedErrorMessage({
        notificationText: 'forms.formSavedIsInvalid',
      });
      return;
    }

    if (!allBrands && checkedBrandIndexes.length === 0 && this.isNewItem) {
      this.notifications.showLocalizedErrorMessage({
        notificationText: 'clients.blacklistSelectBrand',
      });
      return;
    }

    const values: string[] = this.identityFixed
      ? [this.blacklistEditData.value]
      : this.blacklistFormArr.value;

    const blacklistType = {
      id: this.blacklistEditForm.value.blacklistType,
      name: this.getBlaclistTypeName(),
    };

    const upserts = [];

    values.forEach((value: string) => {
      if (allBrands) {
        upserts.push({
          blacklistType,
          value,
          brand: null,
          expire: this.serializedDate(this.globalRowForm.getRawValue().expire),
          notes: (this.globalRowForm.getRawValue().notes || '')
            .toString()
            .trim(),
          id: this.identityFixed
            ? this.existingGlobalRecord?.id
            : undefined,
        });
      } else {
        checkedBrandIndexes.forEach((index: number) => {
          const brand: Brand = this.brands[index];
          const rowValue = this.brandRowAt(index).getRawValue();
          upserts.push({
            blacklistType,
            value,
            brand: { id: brand.id },
            expire: this.serializedDate(rowValue.expire),
            notes: (rowValue.notes || '').toString().trim(),
            id: this.identityFixed
              ? this.existingByBrandId[brand.id]?.id
              : undefined,
          });
        });
      }
    });

    const deleteIds: number[] = [];

    if (this.identityFixed && !allBrands) {
      if (this.existingGlobalRecord) {
        deleteIds.push(this.existingGlobalRecord.id);
      }
      this.brands.forEach((brand: Brand, index: number) => {
        const existing: BlacklistItem = this.existingByBrandId[brand.id];
        if (existing && !checkedBrandIndexes.includes(index)) {
          deleteIds.push(existing.id);
        }
      });
    }

    this.executeSave(upserts, deleteIds);
  }

  executeSave(upserts: any[], deleteIds: number[]): void {
    if (upserts.length === 0 && deleteIds.length === 0) {
      this.dialogRef.close();
      return;
    }

    const deletes$: Observable<any> =
      deleteIds.length > 0
        ? forkJoin(
            deleteIds.map((id: number) =>
              this.blacklistService.deleteInBlacklist(id.toString())
            )
          )
        : of(null);

    this.isSaving = true;
    deletes$
      .pipe(
        switchMap(() =>
          upserts.length > 0
            ? this.blacklistService.saveToBlacklistBulkAdd(upserts)
            : of(null)
        ),
        takeUntil(this._unsubscribe)
      )
      .subscribe({
        next: () => {
          this.notifications.showLocalizedSuccessMessage({
            notificationText: 'clients.blacklistSaveSuccess',
          });
          this.dialogRef.close(true);
        },
        error: () => {
          this.isSaving = false;
          this.notifications.showLocalizedErrorMessage({
            notificationText: 'clients.blacklistSaveError',
          });
        },
      });
  }

  serializedDate(inputDate: Date) {
    if (!inputDate) {
      return null;
    }
    const indexOfTheSelectedYear = inputDate.toString().indexOf('00:00:00');
    if (indexOfTheSelectedYear === -1) {
      return inputDate;
    }
    const firstHalfOfDtae = inputDate
      .toString()
      .substring(0, indexOfTheSelectedYear);

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear().toString();
    const indexOfCurrentYear = currentDate.toString().indexOf(currentYear) + 4;
    const secondHalfOfDate = currentDate
      .toString()
      .substring(indexOfCurrentYear, currentDate.toString().length);
    return new Date(firstHalfOfDtae + ' ' + secondHalfOfDate);
  }

  bulkAddPaste(event: ClipboardEvent) {
    event.preventDefault();
    this.invalidValues = '';
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData.getData('text');
    const arrOfInputs = this.parseValues(pastedText);
    this.blacklistEditForm.controls.value.setValue(
      this.blacklistEditForm.controls.value.value + arrOfInputs.join('\n')
    );

    this.matchValues(arrOfInputs);
    if (this.blacklistFormArr.invalid) {
      this.setErrorValues();
    }
  }

  parseValues(text: string) {
    const creditIds = [];
    const regex =
      this.blacklistEditForm.value.blacklistType === 3
        ? /([+]?\d+)+/gim
        : /(\d+)/gim;
    let match;
    do {
      match = regex.exec(text);
      if (match) {
        creditIds.push(match[1]);
      }
    } while (match);
    return creditIds;
  }

  matchValues(change) {
    this.blacklistFormArr.clear();
    change.forEach((el) => this.addValueToArr(el));
  }

  addValueToArr(value) {
    const newControlForm = this.formBuilder.control(value, this.validators);
    this.blacklistFormArr.push(newControlForm);
  }

  setErrorValues() {
    const valid = [];
    const invalid = [];
    this.blacklistFormArr.controls.forEach((el) =>
      el.valid ? valid.push(el) : invalid.push(el)
    );
    this.invalidValues = invalid.map((el) => el.value).join(', ');
    if (this.blacklistFormArr.controls.length == 0) {
      this.invalidValues = this.blacklistEditForm.value.value;
    }
  }

  getBlaclistTypeName() {
    const type = this.blacklistOriginalTypes.find(
      (el) => el.id === this.blacklistEditForm.value.blacklistType
    );
    return type ? type.name : '';
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
